<script lang="ts">
	//Hooks
	import { onMount } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Services
	import { get_all_fw_versions_gen_gble } from '../../../../services/http';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	import { capFirstChar } from '../../../../helpers/utilities';
	import { getTRVFirmwareVersion } from 'src/helpers/firmware_helpers';
	//Stores
	import { updating_devices, firmwares_gen_gble } from 'src/stores/device-runtime/process';
	import { devices } from 'src/stores/lists/devices';
	//Design system components
	import { Alert, Button, Loader, Text } from '@Shelly/shelly-design-system';
	//Components
	import ControlUpdate from '../../../Controls/Single/ControlUpdate.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	//Comparison
	import { compare } from 'compare-versions';
	//Helpers
	import { isBluTRV } from '../../../../helpers/interface_helpers';

	export let device: Device;
	export let isOffline = false;

	const semVer = /\d+(\.\d+){2,}/gi;

	$: isUpdating = $updating_devices[device?.id] || $updating_devices[real_id(device?.id)];

	$: gatewayId =
		device?.channel >= 2200
			? device?.id?.replace(/_\d{4}$/, '') || real_id(device?.id)?.replace(/_\d{4}$/, '')
			: real_id(device?.id);
	$: sameGatewayTrvDevices =
		gatewayId && device?.channel >= 2200
			? Object.keys($devices).filter((id) => new RegExp(`^${gatewayId}_22\\d{2}$`).test(id))
			: [];
	$: anotherTrvUpdating = sameGatewayTrvDevices.some(
		(trvId) =>
			trvId !== device.id &&
			isBluTRV($devices[trvId]?.type) &&
			($updating_devices[trvId] || $updating_devices[real_id(trvId)]),
	);

	let waitForResponse = false,
		fwVersion: string,
		newFwVersion: string,
		fwVersionLabel: string,
		target_fw = 'stable',
		has_update = false;

	const checkForUpdate = async (force = false) => {
		waitForResponse = true;
		if (force || !$firmwares_gen_gble?.ts || Date.now() > $firmwares_gen_gble.ts + 3600 * 1000) {
			await get_all_fw_versions_gen_gble();
		}
		fwVersion = getTRVFirmwareVersion(device);
		fwVersionLabel = $format('fw_version', {
			values: {
				version: fwVersion,
				default: 'Your firmware version is: {version}.',
			},
		});
		newFwVersion = $firmwares_gen_gble?.[device.type]?.version;
		waitForResponse = false;

		has_update = compare(
			fwVersion?.match(semVer)?.toString() || '1.0.0', //using fallback for fw versions without semver
			newFwVersion?.match(semVer)?.toString(),
			'<',
		);
	};

	onMount(() => {
		checkForUpdate();
	});

	let currentFwVersion = '';
	$: if (fwVersionLabel !== undefined) {
		currentFwVersion = fwVersionLabel.split(': ')[1];
	}
</script>

{#if !fwVersion}
	<div class="d-flex justify-content-center">
		<Loader size="m" />
	</div>
{:else}
	{#if isUpdating}
		<Alert
			type="warning"
			class="mb-s"
			isHeadingBold
			heading={capFirstChar(
				$format('device_status_updating', {
					default: 'updating',
				}),
			)}
			message={$format('info_upate_firmware_wake_up', {
				default: 'Pending update... Wake up the device to execute the update.',
			})}
		/>
	{:else if !has_update}
		<Alert
			type="success"
			class="mb-s"
			isHeadingBold
			heading={$format('device_is_updated', {
				default: 'Your device is up to date:',
			})}
			message={currentFwVersion}
		/>
	{:else if has_update}
		<Text styleType="body-small" class="mb-m ml-s">
			<span class="text-bold d-flex mb-xs"
				>{$format('span_current_firmware_version', {
					default: 'Current Firmware version:',
				})}
			</span>
			<span>{currentFwVersion}</span>
		</Text>
		<Alert
			type="info"
			class="mb-s"
			customIcon="fa-solid fa-arrows-rotate text-primary"
			isHeadingBold
			heading={capFirstChar(
				$format('firmware_available_stable', {
					default: 'available stable version:',
				}),
			)}
			message={newFwVersion}
		/>
		<ControlUpdate hasMarginBottomM hasMarginBottomS {device} {target_fw} {isOffline} />
	{/if}
	<Button
		size="m"
		disabled={isOffline || anotherTrvUpdating}
		width="full"
		variant="secondary"
		loading={waitForResponse}
		on:click={() => checkForUpdate(true)}
	>
		{capFirstChar($format('button_check_for_update', { default: 'check for update' }))}
	</Button>
{/if}
